// Fonctions partagées : libellés de paiement + génération de reçu PDF
import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import { toast } from "sonner";
import { translateDocumentBatch } from "./documentTranslation";
import { translateDocumentLine, translateFrench } from "../i18n/translations";

// Charte couleur Racinefit
const BRAND = { orange: [234, 88, 12], dark: [31, 41, 55], light: [255, 247, 237] };
const documentLanguage = () => document.documentElement.lang || 'fr';
const documentLocale = () => ({ en: 'en-GB', es: 'es-ES', 'zh-CN': 'zh-CN', fr: 'fr-FR' }[documentLanguage()] || 'fr-FR');
const pdfText = (value) => translateFrench(value, documentLanguage());

const drawCjkText = (doc, writeText, text, args) => {
  try {
    const [x, y, options = {}] = args;
    const lines = Array.isArray(text) ? text.map(String) : [String(text)];
    const fontSize = doc.internal.getFontSize();
    const lineHeight = fontSize * 0.3528 * 1.18;
    lines.forEach((line, index) => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      const scale = 4;
      const fontWeight = doc.getFont?.().fontStyle === 'bold' ? 700 : 400;
      context.font = `${fontWeight} ${fontSize * scale}px "Noto Sans SC", "Microsoft YaHei", sans-serif`;
      const widthPx = Math.max(4, Math.ceil(context.measureText(line).width + 8 * scale));
      const heightPx = Math.ceil(fontSize * scale * 1.45);
      canvas.width = widthPx;
      canvas.height = heightPx;
      context.font = `${fontWeight} ${fontSize * scale}px "Noto Sans SC", "Microsoft YaHei", sans-serif`;
      context.textBaseline = 'alphabetic';
      context.fillStyle = doc.getTextColor?.() || '#000000';
      context.fillText(line, 4 * scale, fontSize * scale * 1.08);
      const widthMm = widthPx / scale * 0.264583;
      const heightMm = heightPx / scale * 0.264583;
      const alignedX = options?.align === 'center' ? x - widthMm / 2 : options?.align === 'right' ? x - widthMm : x;
      doc.addImage(canvas.toDataURL('image/png'), 'PNG', alignedX, y + index * lineHeight - heightMm * 0.82, widthMm, heightMm);
    });
  } catch (error) {
    writeText(text, ...args);
  }
};

const localizedPdf = (doc) => {
  const writeText = doc.text.bind(doc);
  const render = (text, args, translate) => {
    const rendered = translate
      ? (Array.isArray(text) ? text.map((item) => translateDocumentLine(String(item), documentLanguage())) : translateDocumentLine(String(text), documentLanguage()))
      : text;
    if (documentLanguage() === 'zh-CN' && typeof document !== 'undefined') drawCjkText(doc, writeText, rendered, args);
    else writeText(rendered, ...args);
  };
  doc.rawText = (text, ...args) => render(text, args, false);
  doc.text = (text, ...args) => render(text, args, true);
  return doc;
};
export const localizePdfDocument = localizedPdf;
export const getDocumentLocale = documentLocale;
export const translatePdfLabel = pdfText;

// En-tête de marque (bandeau + logo texte) — renvoie le Y de départ du contenu
const brandedHeader = (doc, subtitle) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  // Bandeau orange
  doc.setFillColor(BRAND.orange[0], BRAND.orange[1], BRAND.orange[2]);
  doc.rect(0, 0, pageWidth, 26, 'F');
  // Pastille logo (carré arrondi blanc + R orange)
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(14, 6, 14, 14, 2, 2, 'F');
  doc.setTextColor(BRAND.orange[0], BRAND.orange[1], BRAND.orange[2]);
  doc.setFontSize(15); doc.setFont(undefined, 'bold');
  doc.text('R', 21, 16, { align: 'center' });
  // Wordmark
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18); doc.setFont(undefined, 'bold');
  doc.text('RACINEFIT', 32, 14);
  doc.setFontSize(9); doc.setFont(undefined, 'normal');
  doc.text('Salle de sport & bien-être', 32, 20);
  // Sous-titre à droite
  doc.setFontSize(11); doc.setFont(undefined, 'bold');
  doc.text(subtitle || '', pageWidth - 14, 13, { align: 'right' });
  doc.setFontSize(8); doc.setFont(undefined, 'normal');
  doc.text(new Date().toLocaleString(documentLocale()), pageWidth - 14, 19, { align: 'right' });
  // Reset couleur texte pour le contenu
  doc.setTextColor(0, 0, 0);
  return 38;
};

// Libellé de variation vs période précédente (fonction hoistée)
function comparisonLabel(current, previous, periodWord) {
  const cur = current || 0;
  const prev = previous || 0;
  const delta = cur - prev;
  const sign = delta >= 0 ? '+' : '−';
  const abs = Math.abs(delta).toLocaleString();
  let pct = '';
  if (prev !== 0) pct = ` (${delta >= 0 ? '+' : '−'}${Math.abs(Math.round((delta / Math.abs(prev)) * 100))}%)`;
  else if (cur !== 0) pct = ' (nouveau)';
  return `${sign}${abs} CFA${pct} vs ${periodWord} (${prev.toLocaleString()} CFA)`;
}


export const getPaymentMethodLabel = (method) => {
  const methods = {
    'cash': 'Espèces',
    'card': 'Carte bancaire',
    'transfer': 'Virement',
    'check': 'Chèque'
  };
  const label = methods[method];
  return label ? translateFrench(label, documentLanguage()) : method;
};

export const getPaymentStatusLabel = (status) => {
  const statuses = {
    'paid': 'Payé',
    'pending': 'En attente',
    'failed': 'Échoué'
  };
  const label = statuses[status];
  return label ? translateFrench(label, documentLanguage()) : status;
};

export const getSubscriptionPaymentStatusLabel = (status) => {
  const statuses = {
    'paid': 'Payé',
    'pending': 'En attente',
    'overdue': 'En retard',
    'failed': 'Échoué'
  };
  const label = statuses[status];
  return label ? translateFrench(label, documentLanguage()) : status;
};

// Génération du reçu PDF (avec QR code si accessToken fourni)
export const generateReceiptPDF = async (paymentInfo, memberInfo, subscriptionInfo, accessToken = null, output = 'save') => {
  const doc = localizedPdf(new jsPDF());

  // Header - Logo et titre
  doc.setFontSize(24);
  doc.setFont(undefined, 'bold');
  doc.text('RACINEFIT', 105, 25, { align: 'center' });

  doc.setFontSize(12);
  doc.setFont(undefined, 'normal');
  doc.text('Système de gestion de salle de sport', 105, 35, { align: 'center' });

  // Ligne de séparation
  doc.line(20, 45, 190, 45);

  // Titre du reçu
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text('REÇU DE PAIEMENT', 105, 60, { align: 'center' });

  // Informations du reçu
  doc.setFontSize(12);
  doc.setFont(undefined, 'normal');

  const receiptNumber = `REC-${Date.now()}`;
  doc.rawText(`${pdfText("N° de reçu:")} ${receiptNumber}`, 20, 80);
  doc.rawText(`${pdfText("Date:")} ${new Date(paymentInfo.payment_date).toLocaleDateString(documentLocale())}`, 130, 80);

  // Informations du membre
  doc.setFont(undefined, 'bold');
  doc.text('INFORMATIONS DU MEMBRE:', 20, 100);
  doc.setFont(undefined, 'normal');
  doc.rawText(`${pdfText("Nom:")} ${memberInfo.first_name} ${memberInfo.last_name}`, 20, 115);
  doc.rawText(`${pdfText("Email:")} ${memberInfo.email}`, 20, 125);
  doc.rawText(`${pdfText("Téléphone:")} ${memberInfo.phone}`, 20, 135);

  // Informations de l'abonnement
  doc.setFont(undefined, 'bold');
  doc.text('DÉTAILS DE L\'ABONNEMENT:', 20, 155);
  doc.setFont(undefined, 'normal');
  doc.rawText(`${pdfText("Plan:")} ${subscriptionInfo.plan_name}`, 20, 170);
  doc.rawText(`${pdfText("Période:")} ${new Date(subscriptionInfo.start_date).toLocaleDateString(documentLocale())} - ${new Date(subscriptionInfo.end_date).toLocaleDateString(documentLocale())}`, 20, 180);
  doc.rawText(`${pdfText("Durée:")} ${subscriptionInfo.duration_days} ${pdfText("jour(s)")}`, 20, 190);

  // Séance : afficher l'heure d'expiration exacte (valable 4h après l'entrée)
  const isSeancePlan = ['Séance libre', 'Séance complète'].includes(subscriptionInfo.plan_name);
  if (isSeancePlan && subscriptionInfo.end_date) {
    const expD = new Date(subscriptionInfo.end_date);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(190, 70, 0);
    doc.text(`Séance valable jusqu'à ${expD.toLocaleTimeString(documentLocale(), { hour: '2-digit', minute: '2-digit' })} le ${expD.toLocaleDateString(documentLocale())} (4h)`, 20, 198);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'normal');
  }

  // Détails du paiement
  doc.setFont(undefined, 'bold');
  doc.text('DÉTAILS DU PAIEMENT:', 20, 210);
  doc.setFont(undefined, 'normal');
  let py = 225;
  doc.rawText(`${pdfText("Montant:")} ${paymentInfo.amount} CFA`, 20, py); py += 10;
  doc.rawText(`${pdfText("Méthode:")} ${getPaymentMethodLabel(paymentInfo.method)}`, 20, py); py += 10;
  doc.rawText(`${pdfText("Statut:")} ${getPaymentStatusLabel(paymentInfo.status)}`, 20, py); py += 10;
  if (paymentInfo.notes) {
    const noteLines = doc.splitTextToSize(`${pdfText("Notes:")} ${paymentInfo.notes}`, 90);
    doc.rawText(noteLines, 20, py); py += noteLines.length * 6 + 4;
  }

  // Montant restant à payer (échéancier 60/40) + avertissement d'interruption
  const totalPrice = Number(subscriptionInfo.price) || Number(paymentInfo.amount) || 0;
  let remaining = 0;
  if (paymentInfo.is_installment && paymentInfo.installment_number === 2) {
    remaining = 0; // paiement final (solde réglé)
  } else {
    remaining = Math.max(0, totalPrice - Number(paymentInfo.amount || 0));
  }
  doc.setFont(undefined, 'bold');
  doc.text(`Montant restant à payer: ${remaining.toLocaleString()} CFA`, 20, py); py += 8;
  doc.setFont(undefined, 'normal');
  if (remaining > 0) {
    const deadline = new Date(subscriptionInfo.start_date);
    deadline.setDate(deadline.getDate() + 15);
    doc.setTextColor(190, 70, 0);
    const warn = `Attention : l'abonnement sera interrompu si le solde de ${remaining.toLocaleString()} CFA n'est pas réglé avant le ${deadline.toLocaleDateString(documentLocale())} (15 jours après la 1ère échéance).`;
    const wlines = doc.splitTextToSize(warn, 90);
    doc.setFontSize(9);
    doc.text(wlines, 20, py); py += wlines.length * 5;
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
  }

  // Génération du QR Code d'accès si paiement validé
  if (paymentInfo.status === 'paid') {
    if (accessToken) {
      try {
        doc.setFont(undefined, 'bold');
        doc.text('QR CODE D\'ACCÈS:', 120, 155);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        doc.text('Scannez ce code au tourniquet', 120, 165);
        const validLabel = isSeancePlan
          ? `Valide jusqu'à: ${new Date(subscriptionInfo.end_date).toLocaleTimeString(documentLocale(), { hour: '2-digit', minute: '2-digit' })} le ${new Date(subscriptionInfo.end_date).toLocaleDateString(documentLocale())}`
          : `Valide jusqu'au: ${new Date(subscriptionInfo.end_date).toLocaleDateString(documentLocale())}`;
        doc.text(validLabel, 120, 175);

        // Générer le QR code
        const qrCodeDataURL = await QRCode.toDataURL(accessToken, {
          width: 150,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        });


        // Ajouter le QR code au PDF
        doc.addImage(qrCodeDataURL, 'PNG', 120, 185, 60, 60);

        // Instructions d'utilisation
        doc.setFontSize(8);
        doc.text('Présentez ce QR code au scanner', 120, 255);
        doc.text('pour accéder à la salle de sport', 120, 262);

      } catch (error) {
        console.error('Erreur génération QR code:', error);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(10);
        doc.text('QR Code: Erreur de génération', 120, 200);
        doc.text(`Erreur: ${error.message}`, 120, 210);
      }
    } else {
      console.warn('Pas de token d\'accès disponible');
      doc.setFont(undefined, 'bold');
      doc.text('QR CODE D\'ACCÈS:', 120, 155);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(10);
      doc.text('❌ Token non disponible', 120, 175);
      doc.text('Contactez la réception', 120, 185);
    }
  }

  // Pied de page (position dynamique pour ne pas chevaucher l'avertissement)
  const footerLineY = Math.max(270, py + 6);
  doc.line(20, footerLineY, 190, footerLineY);
  doc.setFontSize(10);
  doc.text('Merci pour votre confiance !', 105, footerLineY + 10, { align: 'center' });
  doc.text('Racinefit - Votre partenaire fitness', 105, footerLineY + 20, { align: 'center' });

  // Sauvegarder ou imprimer le PDF
  const fileName = `Reçu_${memberInfo.last_name}_${new Date().toISOString().split('T')[0]}.pdf`;
  if (output === 'print') {
    doc.autoPrint();
    const blobUrl = doc.output('bloburl');
    const win = window.open(blobUrl, '_blank');
    if (!win) doc.save(fileName); // popup bloquée → repli téléchargement
  } else {
    doc.save(fileName);
  }

  // Enregistrer l'historique du reçu généré
  try {
    const receiptRecord = {
      receipt_number: receiptNumber,
      member_id: memberInfo.id,
      member_name: `${memberInfo.first_name} ${memberInfo.last_name}`,
      payment_id: paymentInfo.id,
      subscription_id: subscriptionInfo.id,
      plan_name: subscriptionInfo.plan_name,
      amount: paymentInfo.amount,
      generated_date: new Date().toISOString(),
      file_name: fileName,
      has_qr_code: !!accessToken
    };


    // Vérifier localStorage disponible
    if (typeof(Storage) === "undefined") {
      throw new Error('localStorage non supporté par le navigateur');
    }

    // Sauvegarder dans localStorage pour l'historique avec gestion optimisée
    const existingReceipts = JSON.parse(localStorage.getItem('receipts_history') || '[]');

    existingReceipts.push(receiptRecord);
    localStorage.setItem('receipts_history', JSON.stringify(existingReceipts));

    // Émettre l'événement pour mise à jour immédiate des composants
    window.dispatchEvent(new CustomEvent('localStorageChanged', {
      detail: { key: 'receipts_history', value: existingReceipts }
    }));


    // Notification à l'utilisateur que le reçu a été enregistré
    setTimeout(() => {
      toast.success(`Reçu ${receiptNumber} enregistré dans l'historique`);
    }, 100);

  } catch (error) {
    console.error('❌ Erreur sauvegarde historique reçu:', error);
    toast.error(`Erreur enregistrement historique: ${error.message}`);
  }
};

// Génération d'une fiche membre complète en PDF (infos + historique)
export const generateMemberPDF = (detail) => {
  const { member, subscriptions = [], payments = [], checkins = [], stats = {} } = detail || {};
  const doc = localizedPdf(new jsPDF());
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = brandedHeader(doc, 'Fiche membre');

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(documentLocale()) : '—');
  const fmtDateTime = (d) => (d ? new Date(d).toLocaleString(documentLocale()) : '—');
  const ensureSpace = (needed = 8) => {
    if (y + needed > pageHeight - 15) {
      doc.addPage();
      y = 20;
    }
  };
  const heading = (text) => {
    ensureSpace(14);
    y += 4;
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.text(text, 20, y);
    doc.setDrawColor(230, 120, 30);
    doc.line(20, y + 2, 190, y + 2);
    y += 9;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
  };
  const line = (text) => {
    ensureSpace();
    doc.text(text, 20, y);
    y += 6;
  };

  // Infos membre
  heading('INFORMATIONS DU MEMBRE');
  line(`Nom : ${member.first_name} ${member.last_name}`);
  line(`Email : ${member.email || '—'}`);
  line(`Téléphone : ${member.phone || '—'}`);
  line(`Date de naissance : ${fmtDate(member.date_of_birth)}`);
  line(`Adresse : ${member.address || '—'}`);
  line(`Contact d'urgence : ${member.emergency_contact || '—'} (${member.emergency_phone || '—'})`);
  line(`Statut : ${member.is_active ? 'Actif' : 'Inactif'}`);

  // Résumé
  heading('RÉSUMÉ');
  line(`Abonnements : ${stats.total_subscriptions ?? subscriptions.length} (actifs : ${stats.active_subscriptions ?? 0})`);
  line(`Total payé : ${stats.total_paid ?? 0} CFA`);
  line(`Présences : ${stats.total_checkins ?? checkins.length}`);

  // Abonnements
  heading('ABONNEMENTS');
  if (subscriptions.length === 0) line('Aucun abonnement');
  else subscriptions.forEach((s) => line(`• ${s.plan_name} — du ${fmtDate(s.start_date)} au ${fmtDate(s.end_date)} — ${s.status} — ${s.price} CFA`));

  // Paiements
  heading('PAIEMENTS');
  if (payments.length === 0) line('Aucun paiement');
  else payments.forEach((p) => line(`• ${fmtDate(p.payment_date)} — ${p.amount} CFA — ${getPaymentStatusLabel(p.status)}${p.notes ? ` (${p.notes})` : ''}`));

  // Présences
  heading('PRÉSENCES');
  if (checkins.length === 0) line('Aucune présence');
  else checkins.slice(0, 50).forEach((c) => line(`• ${fmtDateTime(c.check_in_time)}`));

  const fileName = `Fiche_${member.last_name}_${member.first_name}_${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
  toast.success('Fiche PDF générée');
};

// Rapport mensuel de relances (stats + rétention + meilleurs créneaux)
export const generateRelanceReportPDF = ({ stats, retention, bestTime, goal }) => {
  const doc = localizedPdf(new jsPDF());
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = brandedHeader(doc, 'Rapport de relances');
  const ensureSpace = (n = 8) => { if (y + n > pageHeight - 15) { doc.addPage(); y = 20; } };
  const heading = (text) => {
    ensureSpace(14); y += 4;
    doc.setFontSize(13); doc.setFont(undefined, 'bold');
    doc.text(text, 20, y);
    doc.setDrawColor(230, 120, 30); doc.line(20, y + 2, 190, y + 2);
    y += 9; doc.setFontSize(10); doc.setFont(undefined, 'normal');
  };
  const line = (text) => { ensureSpace(); doc.text(text, 20, y); y += 6; };

  heading('ACTIVITÉ DE RELANCES');
  if (stats) {
    line(`Aujourd'hui : ${stats.today}`);
    line(`Cette semaine : ${stats.week}`);
    line(`Ce mois : ${stats.month}`);
    line(`Membres uniques relancés (mois) : ${stats.unique}`);
    line(`Répartition : WhatsApp ${stats.wa} · SMS ${stats.sms}`);
    if (goal != null) line(`Objectif mensuel : ${stats.unique} / ${goal} (${goal > 0 ? Math.min(100, Math.round((stats.unique / goal) * 100)) : 0}%)`);
  }

  heading(`RÉTENTION APRÈS RELANCE (fenêtre ${retention?.window_days ?? '—'} j)`);
  if (retention) {
    line(`Taux de renouvellement global : ${retention.renewal_rate}%`);
    line(`Relancés : ${retention.total_relanced} · Renouvelés : ${retention.renewed} · En attente : ${retention.pending}`);
    if (retention.avg_days_to_renew != null) line(`Délai moyen de renouvellement : ${retention.avg_days_to_renew} j`);
    if (retention.by_channel) {
      line(`WhatsApp : ${retention.by_channel.whatsapp.rate}% (${retention.by_channel.whatsapp.renewed}/${retention.by_channel.whatsapp.total})`);
      line(`SMS : ${retention.by_channel.sms.rate}% (${retention.by_channel.sms.renewed}/${retention.by_channel.sms.total})`);
    }
  }

  heading('MEILLEURS CRÉNEAUX');
  if (bestTime && bestTime.sample_size > 0) {
    line(`Meilleur jour : ${bestTime.best_day?.label || '—'} (${bestTime.best_day?.rate ?? 0}%)`);
    line(`Meilleure plage : ${bestTime.best_slot?.label || '—'} (${bestTime.best_slot?.rate ?? 0}%)`);
    y += 2; line('Par jour :');
    bestTime.by_day.forEach((d) => line(`   ${d.label} : ${d.total > 0 ? `${d.rate}% (${d.renewed}/${d.total})` : '—'}`));
    y += 2; line('Par plage horaire :');
    bestTime.by_slot.forEach((s) => line(`   ${s.label} : ${s.total > 0 ? `${s.rate}% (${s.renewed}/${s.total})` : '—'}`));
  } else {
    line('Données insuffisantes pour analyser les créneaux.');
  }

  doc.save(`Rapport_relances_${new Date().toISOString().split('T')[0]}.pdf`);
  toast.success('Rapport PDF généré');
};

const KIND_LABELS = { welcome: 'Bienvenue', birthday: 'Anniversaire', relance: 'Relance' };

// Rapport détaillé de l'historique des relances (avant remise à zéro / archive mensuelle)
export const generateRelanceHistoryPDF = (logs = [], periodLabel = '') => {
  const doc = localizedPdf(new jsPDF());
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = brandedHeader(doc, periodLabel ? `Archive des relances — ${periodLabel}` : "Historique des relances");
  const ensureSpace = (n = 8) => { if (y + n > pageHeight - 15) { doc.addPage(); y = 20; } };

  doc.setFontSize(10); doc.setFont(undefined, 'normal');
  doc.text(`Total : ${logs.length} relance(s) — export du ${new Date().toLocaleString(documentLocale())}`, 20, y);
  y += 8;

  // En-têtes de tableau
  const drawHeader = () => {
    doc.setFont(undefined, 'bold'); doc.setFontSize(9);
    doc.setDrawColor(230, 120, 30); doc.line(20, y, 190, y); y += 5;
    doc.text('Date', 20, y); doc.text('Membre', 62, y); doc.text('Type', 120, y); doc.text('Canal', 150, y); doc.text('Par', 172, y);
    y += 2; doc.line(20, y, 190, y); y += 5;
    doc.setFont(undefined, 'normal');
  };
  drawHeader();

  const sorted = [...logs].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  sorted.forEach((l) => {
    ensureSpace(7);
    if (y === 20) drawHeader();
    const d = l.created_at ? new Date(l.created_at).toLocaleDateString(documentLocale()) : '—';
    const name = (l.member_name || '—').slice(0, 26);
    const kind = KIND_LABELS[l.kind || 'relance'] || 'Relance';
    const chan = l.channel === 'whatsapp' ? 'WhatsApp' : 'SMS';
    doc.setFontSize(9);
    doc.text(d, 20, y); doc.text(name, 62, y); doc.text(kind, 120, y); doc.text(chan, 150, y); doc.text((l.sent_by || '—').slice(0, 10), 172, y);
    y += 6;
  });

  if (logs.length === 0) { doc.text('Aucune relance enregistrée.', 20, y); }

  const suffix = periodLabel ? periodLabel.replace(/[^0-9A-Za-z-]/g, '_') : new Date().toISOString().split('T')[0];
  doc.save(`Historique_relances_${suffix}.pdf`);
  toast.success(periodLabel ? `Archive PDF ${periodLabel} générée` : "Rapport PDF de l'historique généré");
};


// ---------------------------------------------------------------------------
// Manuel d'utilisation complet (illustré) — PDF téléchargeable
// ---------------------------------------------------------------------------
// Nouveautés par version — AJOUTER une ligne ici à chaque nouvelle version publiée.
// Le manuel PDF affiche automatiquement cette liste (section « Nouveautés par version »).
const MANUAL_CHANGELOG = [
  "v0.10.4 — Parcours Santé : plusieurs canaux de rappel par abonné (WhatsApp + SMS + e-mail), mode TV plein écran du Mur des Réussites ; alerte automatique en cas de baisse des revenus (seuil réglable vs moyenne des 12 derniers mois).",
  "v0.10.0 à 0.10.3 — Parcours Santé complet : objectifs & IMC avec consentement (données chiffrées), points de contrôle avec coach IA, rapports PDF validés par l'admin (e-mail + lien WhatsApp), Mur des Réussites avec félicitations en 1 clic, pesée proposée au pointage, rappels automatiques.",
  "v0.9.x — Tableau de bord personnalisable par rôle (glisser-déposer, modèles prêts à l'emploi), aperçu PDF du bilan multi-mois, comparateur annuel, renforcement de la sécurité et allègement du code.",
  "v0.8.x — SMS & WhatsApp via Twilio avec « canal intelligent », messages d'anniversaire automatiques, sessions renforcées (jetons courts + codes de secours 2FA).",
  "v0.7.x — Sécurité : politique de mots de passe, récupération admin d'urgence, double authentification (e-mail + TOTP).",
  "v0.6.x — Suivi de livraison des e-mails (accusés Resend), anti-doublon de relance 24h, fermeture automatique de la salle, récap hebdomadaire configurable.",
  "v0.5.x — Relances et campagnes par e-mail, sauvegarde mensuelle automatique par e-mail, bilan financier mensuel PDF, journal des remises à zéro.",
  "v0.4.4 — Manuel enrichi (Dépenses, Séances, caisse, sauvegardes) et section « Nouveautés par version ».",
  "v0.4.3 — Carte « Séances du jour » avec compte à rebours en direct et alertes de fin de séance (visuel + son). Cohérence garantie de tous les compteurs (revenus, entrées, caisse).",
  "v0.4.0 — Sauvegarde des données en Excel (classeur multi-onglets) en plus de l'export JSON.",
  "v0.3.9 — Sauvegarde/export JSON des données (admin) et optimisation des rapports.",
  "v0.3.8 — Renforcement de la sécurité : anti-force-brute, politique de mot de passe, sessions renforcées.",
  "v0.3.5 — Pass à la journée (« Séance ») : entrée automatique, expiration 4h et sortie automatique.",
  "v0.3.0 — Module Dépenses : justificatifs, catégories, budgets et circuit de validation.",
  "v0.2.0 — Ouverture/fermeture de la salle avec bilan de journée, caisse du jour et historique.",
  "v0.1.0 — Version initiale : membres, abonnements, entrées/sorties, relances WhatsApp/SMS et rapports.",
];

const MANUAL_SECTIONS = [
  {
    letter: 'T', color: [59, 130, 246], title: 'Tableau de bord', img: 'dashboard.png',
    intro: "Vue d'ensemble de l'activité : membres actifs, abonnements en cours, entrées du jour et revenus du mois. C'est aussi le centre de pilotage des relances, de la caisse et des séances.",
    steps: [
      "Consultez les 4 cartes du haut : « Membres actifs » (membres avec un abonnement en cours), « Abonnements actifs », « Entrées aujourd'hui » et « Revenus du mois ».",
      "Ouvrez / fermez la salle depuis la carte du haut : chaque journée est tracée (heure et personne) et la fermeture génère un bilan PDF téléchargeable.",
      "Suivez la « Caisse du jour » (recettes − dépenses = solde net) et la comparaison avec la veille, ainsi que les recettes du mois.",
      "Carte « Séances du jour » : liste des pass à la journée en cours avec compte à rebours 4h en direct, total encaissé et nombre de séances payées ; une alerte (visuelle + son) prévient le staff 5 min avant la fin puis à l'expiration.",
      "La carte « Rappels d'expiration » liste les abonnements expirés ou expirant bientôt : choisissez le délai (7/15/30 j) et relancez en un clic. Utilisez « Relance groupée » pour contacter tout le monde d'un coup.",
    ],
    tips: [
      "Tous les compteurs financiers (tableau de bord, caisse, rapports) partagent la même source (paiements réels, fuseau de la salle) : ils restent cohérents entre eux.",
      "Un badge orange « À envoyer » sur « Relances programmées » indique des rappels arrivés à échéance.",
    ],
  },
  {
    letter: 'M', color: [234, 88, 12], title: 'Membres', img: 'members.png',
    intro: "Gérez la base de vos adhérents : création, modification, recherche, import en masse, détection et fusion des doublons.",
    steps: [
      "Cliquez « Nouveau membre » et remplissez le formulaire (l'email et le téléphone sont vérifiés automatiquement).",
      "Recherchez par nom, téléphone ou email, et filtrez par statut d'abonnement (Actifs, Expirent bientôt, Expirés, Sans abonnement).",
      "Cliquez sur une carte pour ouvrir la fiche détaillée : abonnements, paiements, présences, historique des relances, et bouton « Imprimer / PDF ».",
      "« Importer » : chargez un fichier CSV/Excel (un modèle est téléchargeable) pour ajouter plusieurs membres.",
      "« Fusionner doublons » (admin) : combinez deux fiches en une seule sans perdre l'historique.",
    ],
    tips: [
      "Un numéro de téléphone en double est bloqué à la création ; un email en double est seulement signalé.",
      "Exportez la liste filtrée en CSV ou Excel pour vos sauvegardes.",
    ],
  },
  {
    letter: 'E', color: [249, 115, 22], title: 'Entrées / Sorties', img: 'checkin.png',
    intro: "Enregistrez les passages des membres à la salle (check-in) pour suivre la fréquentation.",
    steps: [
      "Recherchez le membre puis validez son entrée.",
      "Les entrées du jour alimentent le compteur « Entrées aujourd'hui » du tableau de bord.",
      "L'historique des présences est visible dans la fiche de chaque membre.",
    ],
    tips: ["Le contrôle d'accès par QR code peut automatiser ces entrées via le tourniquet."],
  },
  {
    letter: 'A', color: [16, 185, 129], title: 'Abonnements', img: 'subscriptions.png',
    intro: "Créez, renouvelez et suivez les abonnements et leurs paiements (payés / en attente), y compris les pass à la journée (« Séance »).",
    steps: [
      "Créez un abonnement en sélectionnant le membre, le plan, la date de début, la durée et le prix.",
      "Suivez les onglets « Actifs » et « Expirés », ainsi que les paiements « Payés » et « En attente ».",
      "Renouvelez un abonnement expiré en un clic : la nouvelle période démarre au bon moment.",
      "Un premier paiement encaissé déclenche automatiquement le message de bienvenue.",
      "Pass à la journée (« Séance libre » / « Séance complète ») : l'encaissement crée automatiquement l'entrée du membre et démarre un compte à rebours de 4h ; la sortie est enregistrée automatiquement à l'expiration.",
    ],
    tips: [
      "Les compteurs d'abonnements respectent la période et les filtres sélectionnés.",
      "Le reçu PDF d'une séance indique l'heure limite de validité (4h) et un QR d'accès.",
    ],
  },
  {
    letter: 'S', color: [16, 185, 129], title: 'Parcours Santé', img: 'health.png',
    intro: "Suivi santé des abonnés : objectif avec date butoir, points de contrôle réguliers, coach IA et bilans PDF. Les données de santé sont chiffrées et collectées avec le consentement explicite de l'abonné.",
    steps: [
      "Cliquez « Nouveau parcours » : choisissez l'abonné, l'objectif (perte de poids, prise de masse, endurance…), la durée (défaut 12 semaines) et saisissez poids / taille — l'IMC est calculé automatiquement.",
      "Cochez la case de consentement (obligatoire) : l'abonné accepte la collecte de ses données de santé, stockées chiffrées.",
      "Toutes les 3 semaines (réglable), enregistrez le point de contrôle (poids, tour de taille) : un message d'encouragement du coach IA s'affiche et la courbe de progression se met à jour.",
      "Les contrôles en retard apparaissent sur le Tableau de bord (relance en 1 clic) et à l'Entrée du membre (bouton « Pesée » proposé au pointage).",
      "Envoyez le rapport PDF (progression ou bilan final) : la demande passe par l'approbation de l'administrateur, puis part par e-mail (pièce jointe) et WhatsApp/SMS (lien sécurisé).",
      "À la date butoir, clôturez le parcours : bilan complet (objectif atteint ou non, recommandations personnalisées) et possibilité de fixer un nouvel objectif.",
    ],
    tips: [
      "Le « Mur des Réussites » met à l'honneur les objectifs atteints : félicitez l'abonné en 1 clic pour renforcer sa fidélité.",
      "La vue d'ensemble (taux de réussite, répartition des objectifs, engagement) est anonymisée : aucune donnée nominative.",
    ],
  },
  {
    letter: 'D', color: [217, 119, 6], title: 'Dépenses', img: 'expenses.png',
    intro: "Enregistrez et suivez les dépenses de la salle avec justificatifs, catégories, budgets et circuit de validation.",
    steps: [
      "Ajoutez une dépense : montant, catégorie, date et note. Joignez un justificatif (photo / PDF) si besoin.",
      "Le staff soumet ses dépenses ; l'administrateur les approuve ou les rejette (circuit de validation).",
      "Suivez les totaux « En attente » et « Approuvées », et définissez des budgets par catégorie.",
      "Les dépenses approuvées sont déduites de la caisse du jour et apparaissent dans les rapports (jour, mois, année).",
    ],
    tips: [
      "Chaque membre du staff ne voit et ne télécharge que ses propres justificatifs ; l'administrateur voit tout.",
      "Le solde net de caisse = recettes encaissées − dépenses approuvées.",
    ],
  },
  {
    letter: 'R', color: [37, 99, 235], title: 'Relances & Rétention', img: 'dashboard.png',
    intro: "Le cœur de la fidélisation : contactez les membres par WhatsApp ou SMS avec des modèles pré-remplis, et mesurez l'effet des relances.",
    steps: [
      "Depuis un rappel d'expiration ou une fiche membre, cliquez « Relancer ».",
      "Choisissez un modèle : les variables {prenom}, {plan}, {date} et {offre} sont remplacées automatiquement.",
      "Vérifiez l'aperçu du message (bulle de discussion) — une alerte s'affiche si une variable n'est pas remplie.",
      "Envoyez via WhatsApp ou SMS : l'application de messagerie s'ouvre avec le texte pré-rempli.",
      "Suivez le taux de rétention, le meilleur créneau d'envoi et l'impact des offres.",
    ],
    tips: [
      "Le mode « anti-doublon » évite de relancer deux fois le même membre trop vite.",
      "Personnalisez vos modèles et vos offres (bienvenue / anniversaire) avec une date limite pour créer l'urgence.",
    ],
  },
  {
    letter: 'P', color: [99, 102, 241], title: 'Relances programmées', img: 'dashboard.png',
    intro: "Planifiez une relance à une date et une heure précises. Le jour venu, elle apparaît dans « Relances programmées » avec une notification ; l'envoi se fait en un clic (pas d'envoi automatique).",
    steps: [
      "Dans une fenêtre de relance, cliquez « Programmer pour plus tard ».",
      "Choisissez la date et l'heure, puis « Programmer ».",
      "À l'échéance, la relance passe en « À envoyer » sur le tableau de bord ; cliquez WhatsApp ou SMS pour l'envoyer.",
      "Vous pouvez annuler une relance programmée tant qu'elle n'a pas été envoyée.",
    ],
    tips: ["Une notification quotidienne vous rappelle les relances arrivées à échéance."],
  },
  {
    letter: 'H', color: [139, 92, 246], title: 'Historique & Rapports', img: 'history.png',
    intro: "Retrouvez tout l'historique des relances et générez des rapports PDF/Excel.",
    steps: [
      "Filtrez l'historique par type (relance / bienvenue / anniversaire), canal ou nom.",
      "Exportez en CSV, Excel ou PDF pour vos archives.",
      "Un archivage PDF automatique est réalisé chaque 1er du mois.",
    ],
    tips: ["La réinitialisation de l'historique génère d'abord un PDF de sauvegarde."],
  },
  {
    letter: 'C', color: [220, 38, 38], title: "Contrôle d'accès", img: 'access.png',
    intro: "Gérez l'accès physique (QR codes / tourniquet) et consultez le journal des accès.",
    steps: [
      "Générez ou validez les jetons d'accès des membres.",
      "Consultez le journal des accès, filtrez par date et exportez-le (CSV/PDF).",
    ],
    tips: ["Le filtre par date par défaut affiche les 30 derniers jours."],
  },
  {
    letter: 'U', color: [31, 41, 55], title: 'Utilisateurs (staff)', img: 'users.png',
    intro: "Réservé aux administrateurs : gérez les comptes du personnel, l'historique d'ouverture de la salle et les sauvegardes de données.",
    steps: [
      "Créez un utilisateur (admin ou staff) et définissez son mot de passe (8 caractères minimum).",
      "Activez/désactivez un compte, ou réinitialisez son mot de passe.",
      "Consultez le « Journal des connexions » et l'« Historique d'ouverture/fermeture de la salle ».",
      "Sauvegardez toutes les données en un clic : export « JSON » (sauvegarde complète) ou « Excel » (classeur multi-onglets : membres, abonnements, paiements, dépenses…).",
    ],
    tips: [
      "La connexion est insensible à la casse et aux espaces sur l'identifiant ; le mot de passe reste sensible à la casse.",
      "Après 5 échecs de connexion, l'accès est bloqué 15 minutes (protection anti-force-brute).",
    ],
  },
  {
    letter: 'O', color: [2, 132, 199], title: 'Mode hors ligne (PWA)',
    intro: "Racinefit fonctionne même sans connexion : consultation, navigation et saisie continuent, puis tout se synchronise au retour du réseau.",
    steps: [
      "Installez l'application sur votre écran d'accueil pour un accès rapide.",
      "En cas de coupure, continuez à travailler : les actions sont mises en file d'attente (un bandeau indique le mode hors ligne).",
      "Au retour de la connexion, un message confirme la synchronisation.",
    ],
    tips: ["En cas d'affichage figé après une mise à jour, fermez puis rouvrez l'application."],
  },
  {
    letter: 'N', color: [147, 51, 234], title: 'Nouveautés par version',
    intro: "Historique des principales évolutions de l'application. Ce manuel affiche toujours la version en cours (voir la page de couverture) et cette liste est mise à jour à chaque nouvelle version.",
    steps: MANUAL_CHANGELOG,
    tips: ["La version installée est indiquée en bas de l'onglet « À propos » et sur la couverture de ce manuel."],
  },
];

// Charge une image (public/) et renvoie { dataURL, w, h } via canvas (JPEG compressé)
const loadManualImage = (url) => new Promise((resolve) => {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve({ dataURL: canvas.toDataURL('image/jpeg', 0.82), w: img.naturalWidth, h: img.naturalHeight });
      } catch (e) { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  } catch (e) { resolve(null); }
});

export const generateUserManualPDF = async ({ version = '', buildDate = '', language = document.documentElement.lang || 'fr' } = {}) => {
  const doc = localizedPdf(new jsPDF());
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const base = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) ? process.env.PUBLIC_URL : '';

  const manualTexts = MANUAL_SECTIONS.flatMap((section) => [section.title, section.intro, ...section.steps, ...(section.tips || [])]);
  const translatedTexts = await translateDocumentBatch(manualTexts, language, 'manual');
  let translationIndex = 0;
  const sections = MANUAL_SECTIONS.map((section) => ({
    ...section,
    title: translatedTexts[translationIndex++],
    intro: translatedTexts[translationIndex++],
    steps: section.steps.map(() => translatedTexts[translationIndex++]),
    tips: (section.tips || []).map(() => translatedTexts[translationIndex++]),
  }));

  // Manifest des captures (version au moment de la capture)
  let captureInfo = null;
  try {
    const r = await fetch(`${base}/manual/manifest.json`);
    if (r.ok) captureInfo = await r.json();
  } catch (e) { /* ignore */ }

  // Pré-charge les captures d'écran
  const imgCache = {};
  await Promise.all(
    [...new Set(MANUAL_SECTIONS.map((s) => s.img).filter(Boolean))].map(async (name) => {
      imgCache[name] = await loadManualImage(`${base}/manual/${name}`);
    })
  );

  // --- Page de couverture ---
  doc.setFillColor(BRAND.orange[0], BRAND.orange[1], BRAND.orange[2]);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(pageWidth / 2 - 20, 60, 40, 40, 6, 6, 'F');
  doc.setTextColor(BRAND.orange[0], BRAND.orange[1], BRAND.orange[2]);
  doc.setFontSize(40); doc.setFont(undefined, 'bold');
  doc.text('R', pageWidth / 2, 88, { align: 'center' });
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(30); doc.setFont(undefined, 'bold');
  doc.text('RACINEFIT', pageWidth / 2, 125, { align: 'center' });
  doc.setFontSize(18); doc.setFont(undefined, 'normal');
  doc.text("Manuel d'utilisation", pageWidth / 2, 140, { align: 'center' });
  doc.setFontSize(11);
  doc.text('Gestion de salle de sport & suivi des membres', pageWidth / 2, 152, { align: 'center' });
  doc.setFontSize(9);
  const meta = [version ? `Version ${version}` : '', buildDate ? `Build ${buildDate}` : '', `Généré le ${new Date().toLocaleDateString(documentLocale())}`].filter(Boolean).join('  •  ');
  doc.text(meta, pageWidth / 2, 170, { align: 'center' });
  if (captureInfo && captureInfo.version) {
    doc.setFontSize(8);
    doc.text(`Captures d'écran de la version ${captureInfo.version}`, pageWidth / 2, 178, { align: 'center' });
  }

  // --- Sommaire ---
  doc.addPage();
  let y = brandedHeader(doc, 'Sommaire');
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(15); doc.setFont(undefined, 'bold');
  doc.text('Sommaire', 20, y); y += 10;
  doc.setFontSize(11); doc.setFont(undefined, 'normal');
  sections.forEach((s, i) => {
    doc.setFillColor(s.color[0], s.color[1], s.color[2]);
    doc.roundedRect(20, y - 5, 7, 7, 1.5, 1.5, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont(undefined, 'bold');
    doc.text(s.letter, 23.5, y, { align: 'center' });
    doc.setTextColor(0, 0, 0); doc.setFontSize(11); doc.setFont(undefined, 'normal');
    doc.text(`${i + 1}. ${s.title}`, 32, y);
    y += 10;
  });

  const ensureSpace = (n) => { if (y + n > pageHeight - 18) { doc.addPage(); y = 20; } };

  // --- Sections détaillées ---
  sections.forEach((s, idx) => {
    doc.addPage();
    y = 20;
    // Bandeau de section coloré + pastille lettre
    doc.setFillColor(s.color[0], s.color[1], s.color[2]);
    doc.rect(0, 0, pageWidth, 24, 'F');
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(14, 5, 14, 14, 2, 2, 'F');
    doc.setTextColor(s.color[0], s.color[1], s.color[2]);
    doc.setFontSize(14); doc.setFont(undefined, 'bold');
    doc.text(s.letter, 21, 15, { align: 'center' });
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(15); doc.setFont(undefined, 'bold');
    doc.text(`${idx + 1}. ${s.title}`, 32, 15);
    y = 34;

    // Capture d'écran (si disponible)
    const shot = s.img ? imgCache[s.img] : null;
    if (shot && shot.dataURL) {
      const maxW = pageWidth - 40;
      const maxH = 78;
      let w = maxW;
      let h = w * (shot.h / shot.w);
      if (h > maxH) { h = maxH; w = h * (shot.w / shot.h); }
      const x = (pageWidth - w) / 2;
      // cadre léger autour de la capture
      doc.setDrawColor(220, 220, 220);
      doc.setFillColor(245, 245, 245);
      doc.roundedRect(x - 1.5, y - 1.5, w + 3, h + 3, 2, 2, 'FD');
      try { doc.addImage(shot.dataURL, 'JPEG', x, y, w, h); } catch (e) {}
      y += h + 3;
      doc.setTextColor(150, 150, 150); doc.setFontSize(7.5); doc.setFont(undefined, 'italic');
      doc.text(`Aperçu — onglet « ${s.title} »`, pageWidth / 2, y + 3, { align: 'center' });
      doc.setFont(undefined, 'normal');
      y += 8;
    }

    // Intro
    doc.setTextColor(60, 60, 60); doc.setFontSize(10); doc.setFont(undefined, 'italic');
    const introLines = doc.splitTextToSize(s.intro, pageWidth - 40);
    ensureSpace(introLines.length * 5 + 4);
    doc.text(introLines, 20, y); y += introLines.length * 5 + 4;
    doc.setFont(undefined, 'normal');

    // Étapes
    doc.setTextColor(s.color[0], s.color[1], s.color[2]);
    doc.setFontSize(12); doc.setFont(undefined, 'bold');
    ensureSpace(10); doc.text('Étapes', 20, y); y += 7;
    doc.setTextColor(0, 0, 0); doc.setFontSize(10); doc.setFont(undefined, 'normal');
    s.steps.forEach((step, i) => {
      const lines = doc.splitTextToSize(step, pageWidth - 46);
      ensureSpace(lines.length * 5 + 3);
      doc.setFillColor(s.color[0], s.color[1], s.color[2]);
      doc.circle(23, y - 1.5, 2.6, 'F');
      doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont(undefined, 'bold');
      doc.text(String(i + 1), 23, y - 0.3, { align: 'center' });
      doc.setTextColor(0, 0, 0); doc.setFontSize(10); doc.setFont(undefined, 'normal');
      doc.text(lines, 29, y);
      y += lines.length * 5 + 3;
    });

    // Astuces
    if (s.tips && s.tips.length) {
      y += 2;
      const tipText = s.tips.map((t) => `• ${t}`);
      const allLines = tipText.flatMap((t) => doc.splitTextToSize(t, pageWidth - 50));
      const boxH = allLines.length * 5 + 12;
      ensureSpace(boxH);
      doc.setFillColor(255, 247, 237);
      doc.setDrawColor(s.color[0], s.color[1], s.color[2]);
      doc.roundedRect(20, y, pageWidth - 40, boxH, 3, 3, 'FD');
      doc.setTextColor(s.color[0], s.color[1], s.color[2]);
      doc.setFontSize(10); doc.setFont(undefined, 'bold');
      doc.text('Astuces', 26, y + 7);
      doc.setTextColor(60, 60, 60); doc.setFontSize(9); doc.setFont(undefined, 'normal');
      doc.text(allLines, 26, y + 13);
      y += boxH + 6;
    }
  });

  // Pieds de page (numéros) sur toutes les pages sauf la couverture
  const total = doc.getNumberOfPages();
  for (let p = 2; p <= total; p++) {
    doc.setPage(p);
    doc.setTextColor(150, 150, 150); doc.setFontSize(8); doc.setFont(undefined, 'normal');
    doc.text(`Racinefit — Manuel d'utilisation`, 20, pageHeight - 8);
    doc.text(`Page ${p - 1} / ${total - 1}`, pageWidth - 20, pageHeight - 8, { align: 'right' });
  }

  doc.save(`Manuel_Racinefit_${new Date().toISOString().split('T')[0]}.pdf`);
  toast.success("Manuel d'utilisation PDF généré");
};

// ---------------------------------------------------------------------------
// Bilan de la journée (ouverture/fermeture de salle) — PDF
// ---------------------------------------------------------------------------
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString(documentLocale(), { hour: '2-digit', minute: '2-digit' }) : '—';

export const generateDayReportPDF = ({ session = {}, payments = [], checkinsCount = 0, newMembers = [], newSubscriptions = [], relances = [], expenses = [], totalExpenses = 0, gymName = 'Racinefit', day = null } = {}) => {
  const doc = localizedPdf(new jsPDF());
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = brandedHeader(doc, `Bilan de la journée`);
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(16); doc.setFont(undefined, 'bold');
  doc.text('Bilan de la journée', 20, y);
  doc.setFontSize(11); doc.setFont(undefined, 'normal');
  const reportDate = day ? new Date(`${day}T12:00:00`) : new Date();
  doc.text(reportDate.toLocaleDateString(documentLocale(), { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }), 20, y + 7);
  y += 18;

  // Horaires
  doc.setFillColor(255, 247, 237);
  doc.roundedRect(20, y, pageWidth - 40, 20, 3, 3, 'F');
  doc.setFontSize(11); doc.setFont(undefined, 'bold'); doc.setTextColor(234, 88, 12);
  doc.text("Horaires", 26, y + 8);
  doc.setFont(undefined, 'normal'); doc.setTextColor(0, 0, 0);
  doc.text(`Ouverture : ${fmtTime(session.opened_at)}${session.opened_by ? ` (${session.opened_by})` : ''}`, 26, y + 15);
  doc.text(`Fermeture : ${fmtTime(session.closed_at)}${session.closed_by ? ` (${session.closed_by})` : ''}`, 110, y + 15);
  y += 28;

  // Caisse
  const paidToday = payments.filter((p) => p.status === 'paid');
  const total = paidToday.reduce((s, p) => s + (p.amount || 0), 0);
  const byMethod = {};
  paidToday.forEach((p) => { const m = p.method || 'autre'; byMethod[m] = (byMethod[m] || 0) + (p.amount || 0); });
  doc.setFontSize(13); doc.setFont(undefined, 'bold'); doc.setTextColor(16, 129, 96);
  doc.text('Points de caisse', 20, y); y += 8;
  doc.setFontSize(12); doc.setTextColor(0, 0, 0);
  doc.text(`Total encaissé : ${total.toLocaleString()} CFA`, 24, y); y += 7;
  doc.setFontSize(10); doc.setFont(undefined, 'normal');
  doc.text(`Nombre de paiements : ${paidToday.length}`, 24, y); y += 7;
  Object.entries(byMethod).forEach(([m, amt]) => {
    doc.text(`• ${getPaymentMethodLabel ? getPaymentMethodLabel(m) : m} : ${amt.toLocaleString()} CFA`, 28, y); y += 6;
  });
  y += 4;

  // Dépenses (sorties de caisse)
  const totalExp = totalExpenses || expenses.reduce((s, e) => s + (e.amount || 0), 0);
  doc.setFontSize(13); doc.setFont(undefined, 'bold'); doc.setTextColor(220, 38, 38);
  doc.text('Dépenses (sorties de caisse)', 20, y); y += 8;
  doc.setFontSize(11); doc.setFont(undefined, 'normal'); doc.setTextColor(0, 0, 0);
  doc.text(`Total dépenses : ${totalExp.toLocaleString()} CFA`, 24, y); y += 7;
  doc.setFontSize(10);
  if (expenses.length === 0) { doc.text('• Aucune dépense', 28, y); y += 6; }
  expenses.forEach((e) => {
    doc.text(`• ${e.category || 'Autres'} : ${(e.amount || 0).toLocaleString()} CFA${e.description ? ` (${e.description})` : ''}`, 28, y); y += 6;
  });
  y += 3;

  // Solde net de caisse
  const net = total - totalExp;
  doc.setFillColor(net >= 0 ? 240 : 254, net >= 0 ? 253 : 242, net >= 0 ? 244 : 242);
  doc.roundedRect(20, y, doc.internal.pageSize.getWidth() - 40, 16, 3, 3, 'F');
  doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.setTextColor(net >= 0 ? 16 : 220, net >= 0 ? 129 : 38, net >= 0 ? 96 : 38);
  doc.text(`Solde net de caisse : ${net.toLocaleString()} CFA`, 26, y + 10);
  doc.setTextColor(0, 0, 0); doc.setFont(undefined, 'normal');
  y += 22;

  // Événements de la journée
  doc.setFontSize(13); doc.setFont(undefined, 'bold'); doc.setTextColor(37, 99, 235);
  doc.text('Événements de la journée', 20, y); y += 8;
  doc.setFontSize(10); doc.setFont(undefined, 'normal'); doc.setTextColor(0, 0, 0);
  const rows = [
    `Entrées (check-ins) : ${checkinsCount}`,
    `Nouveaux membres : ${newMembers.length}`,
    `Nouveaux abonnements : ${newSubscriptions.length}`,
    `Relances envoyées : ${relances.length}`,
  ];
  rows.forEach((r) => { doc.text(`• ${r}`, 24, y); y += 6; });
  y += 2;

  // Détail nouveaux membres / abonnements (facultatif, compact)
  if (newMembers.length) {
    doc.setFont(undefined, 'bold'); doc.text('Nouveaux membres :', 24, y); y += 6; doc.setFont(undefined, 'normal');
    newMembers.slice(0, 15).forEach((m) => { doc.text(`- ${m.first_name} ${m.last_name}`, 28, y); y += 5; });
    y += 2;
  }
  if (paidToday.length) {
    doc.setFont(undefined, 'bold'); doc.text('Paiements encaissés :', 24, y); y += 6; doc.setFont(undefined, 'normal');
    paidToday.slice(0, 15).forEach((p) => {
      doc.text(`- ${(p.amount || 0).toLocaleString()} CFA${p.notes ? ` (${p.notes})` : ''}`, 28, y); y += 5;
    });
  }

  // Pied de page
  const ph = doc.internal.pageSize.getHeight();
  doc.setTextColor(150, 150, 150); doc.setFontSize(8);
  doc.text(`${gymName} — Bilan généré le ${new Date().toLocaleString(documentLocale())}`, 20, ph - 10);

  doc.save(`Bilan_${gymName}_${day || new Date().toISOString().split('T')[0]}.pdf`);
  toast.success('Bilan de la journée généré');
};


export const generateMonthReportPDF = (report = {}, gymName = 'Racinefit') => {
  const doc = localizedPdf(new jsPDF());
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = brandedHeader(doc, 'Bilan mensuel');
  doc.setTextColor(0, 0, 0);

  const [yy, mm] = (report.month || '').split('-');
  const monthLabel = yy && mm
    ? new Date(Number(yy), Number(mm) - 1, 1).toLocaleDateString(documentLocale(), { month: 'long', year: 'numeric' })
    : (report.month || '');
  doc.setFontSize(16); doc.setFont(undefined, 'bold');
  doc.text('Bilan mensuel consolidé', 20, y);
  doc.setFontSize(12); doc.setFont(undefined, 'normal');
  doc.text(monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1), 20, y + 7);
  y += 18;

  // Encadré recettes
  doc.setFillColor(240, 253, 244);
  doc.roundedRect(20, y, pageWidth - 40, 22, 3, 3, 'F');
  doc.setFontSize(11); doc.setFont(undefined, 'bold'); doc.setTextColor(16, 129, 96);
  doc.text('Recettes du mois', 26, y + 8);
  doc.setFontSize(15); doc.setTextColor(0, 0, 0);
  doc.text(`${(report.total_revenue || 0).toLocaleString()} CFA`, 26, y + 17);
  doc.setFontSize(9); doc.setFont(undefined, 'normal'); doc.setTextColor(90, 90, 90);
  doc.text(`Jours d'ouverture : ${report.days_open || 0}`, pageWidth - 26, y + 12, { align: 'right' });
  y += 30;

  // Recettes par méthode
  doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.setTextColor(16, 129, 96);
  doc.text('Recettes par mode de paiement', 20, y); y += 7;
  doc.setFontSize(10); doc.setFont(undefined, 'normal'); doc.setTextColor(0, 0, 0);
  const methods = Object.entries(report.by_method || {});
  if (methods.length === 0) { doc.text('• Aucune recette', 24, y); y += 6; }
  methods.forEach(([m, amt]) => {
    doc.text(`• ${getPaymentMethodLabel ? getPaymentMethodLabel(m) : m} : ${amt.toLocaleString()} CFA`, 24, y); y += 6;
  });
  y += 4;

  // Dépenses par catégorie + solde net
  doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.setTextColor(220, 38, 38);
  doc.text('Dépenses par catégorie', 20, y); y += 7;
  doc.setFontSize(10); doc.setFont(undefined, 'normal'); doc.setTextColor(0, 0, 0);
  const cats = Object.entries(report.expenses_by_category || {});
  if (cats.length === 0) { doc.text('• Aucune dépense', 24, y); y += 6; }
  cats.forEach(([c, amt]) => { doc.text(`• ${c} : ${amt.toLocaleString()} CFA`, 24, y); y += 6; });
  doc.setFont(undefined, 'bold'); doc.setTextColor(220, 38, 38);
  doc.text(`Total dépenses : ${(report.total_expenses || 0).toLocaleString()} CFA`, 24, y); y += 8;
  const netM = (report.net_revenue != null) ? report.net_revenue : ((report.total_revenue || 0) - (report.total_expenses || 0));
  doc.setFillColor(netM >= 0 ? 240 : 254, netM >= 0 ? 253 : 242, netM >= 0 ? 244 : 242);
  doc.roundedRect(20, y, pageWidth - 40, 16, 3, 3, 'F');
  doc.setFontSize(13); doc.setTextColor(netM >= 0 ? 16 : 220, netM >= 0 ? 129 : 38, netM >= 0 ? 96 : 38);
  doc.text(`Solde net (recettes − dépenses) : ${netM.toLocaleString()} CFA`, 26, y + 10);
  doc.setTextColor(0, 0, 0); doc.setFont(undefined, 'normal');
  y += 22;

  // Comparaison avec le mois précédent
  if (report.prev) {
    doc.setFontSize(11); doc.setFont(undefined, 'bold'); doc.setTextColor(37, 99, 235);
    doc.text('Évolution vs mois précédent', 20, y); y += 6;
    doc.setFontSize(9.5); doc.setFont(undefined, 'normal'); doc.setTextColor(60, 60, 60);
    doc.text(`• Recettes : ${comparisonLabel(report.total_revenue, report.prev.total_revenue, 'mois préc.')}`, 24, y); y += 5.5;
    doc.text(`• Dépenses : ${comparisonLabel(report.total_expenses, report.prev.total_expenses, 'mois préc.')}`, 24, y); y += 5.5;
    doc.text(`• Solde net : ${comparisonLabel(netM, report.prev.net_revenue, 'mois préc.')}`, 24, y); y += 8;
    doc.setTextColor(0, 0, 0);
  }

  // Fréquentation & croissance
  doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.setTextColor(37, 99, 235);
  doc.text('Fréquentation & croissance', 20, y); y += 7;
  doc.setFontSize(10); doc.setFont(undefined, 'normal'); doc.setTextColor(0, 0, 0);
  [
    `Entrées (check-ins) : ${report.checkins_count || 0}`,
    `Membres uniques présents : ${report.unique_members_present || 0}`,
    `Nouveaux membres : ${(report.new_members || []).length}`,
    `Nouveaux abonnements : ${(report.new_subscriptions || []).length}`,
    `Relances envoyées : ${report.relances_count || 0}`,
  ].forEach((r) => { doc.text(`• ${r}`, 24, y); y += 6; });
  y += 4;

  // Mini graphique recettes par jour
  const byDay = report.by_day || [];
  if (byDay.length) {
    doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.setTextColor(234, 88, 12);
    doc.text('Recettes par jour', 20, y); y += 6;
    const maxAmt = Math.max(...byDay.map((d) => d.amount), 1);
    const chartH = 40, chartW = pageWidth - 40, baseY = y + chartH;
    const barW = Math.min(8, (chartW / byDay.length) - 2);
    const gap = (chartW - barW * byDay.length) / Math.max(byDay.length - 1, 1);
    doc.setDrawColor(220, 220, 220);
    doc.line(20, baseY, 20 + chartW, baseY);
    byDay.forEach((d, i) => {
      const h = (d.amount / maxAmt) * chartH;
      const x = 20 + i * (barW + gap);
      doc.setFillColor(234, 88, 12);
      doc.rect(x, baseY - h, barW, h, 'F');
    });
    y = baseY + 6;
    doc.setFontSize(7); doc.setTextColor(120, 120, 120); doc.setFont(undefined, 'normal');
    doc.text(`Sommet : ${maxAmt.toLocaleString()} CFA`, 20, y);
    y += 8;
  }

  const ph = doc.internal.pageSize.getHeight();
  doc.setTextColor(150, 150, 150); doc.setFontSize(8);
  doc.text(`${gymName} — Bilan mensuel généré le ${new Date().toLocaleString(documentLocale())}`, 20, ph - 10);

  doc.save(`Bilan_mensuel_${gymName}_${report.month || ''}.pdf`);
  toast.success('Bilan mensuel généré');
};

const STATUS_LABEL_FR = { pending: 'En attente', approved: 'Approuvée', rejected: 'Refusée' };

export const generateExpensesListPDF = (expenses = [], filters = {}, gymName = 'Racinefit') => {
  const doc = localizedPdf(new jsPDF());
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = brandedHeader(doc, 'Liste des dépenses');
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(15); doc.setFont(undefined, 'bold');
  doc.text('Liste des dépenses', 20, y); y += 7;
  doc.setFontSize(9); doc.setFont(undefined, 'normal'); doc.setTextColor(90, 90, 90);
  const parts = [];
  if (filters.from) parts.push(`Du ${new Date(filters.from).toLocaleDateString(documentLocale())}`);
  if (filters.to) parts.push(`au ${new Date(filters.to).toLocaleDateString(documentLocale())}`);
  if (filters.category && filters.category !== 'all') parts.push(`Catégorie : ${filters.category}`);
  if (filters.status && filters.status !== 'all') parts.push(`Statut : ${STATUS_LABEL_FR[filters.status] || filters.status}`);
  doc.text(parts.length ? parts.join(' · ') : 'Toutes les dépenses', 20, y); y += 8;
  doc.setTextColor(0, 0, 0);

  // En-têtes du tableau
  const cols = [
    { t: 'Date', x: 20 }, { t: 'Catégorie', x: 50 }, { t: 'Description', x: 90 },
    { t: 'Statut', x: 150 }, { t: 'Montant', x: pageWidth - 20, align: 'right' },
  ];
  const drawHead = () => {
    doc.setFillColor(234, 88, 12); doc.rect(18, y - 5, pageWidth - 36, 8, 'F');
    doc.setFontSize(9); doc.setFont(undefined, 'bold'); doc.setTextColor(255, 255, 255);
    cols.forEach((c) => doc.text(c.t, c.x, y, c.align ? { align: c.align } : undefined));
    y += 7; doc.setTextColor(0, 0, 0); doc.setFont(undefined, 'normal');
  };
  drawHead();

  let total = 0;
  const approvedTotal = expenses.filter((e) => e.status === 'approved').reduce((s, e) => s + (e.amount || 0), 0);
  doc.setFontSize(9);
  expenses.forEach((e, i) => {
    if (y > pageHeight - 25) { doc.addPage(); y = 20; drawHead(); }
    if (i % 2 === 0) { doc.setFillColor(248, 248, 248); doc.rect(18, y - 4, pageWidth - 36, 7, 'F'); }
    const d = e.expense_date ? new Date(e.expense_date).toLocaleDateString(documentLocale()) : '';
    doc.text(d, 20, y);
    doc.rawText(String(e.category || '').slice(0, 18), 50, y);
    doc.rawText(String(e.description || '').slice(0, 32), 90, y);
    doc.rawText(pdfText(STATUS_LABEL_FR[e.status] || e.status || ''), 150, y);
    doc.text(`${(e.amount || 0).toLocaleString()} CFA`, pageWidth - 20, y, { align: 'right' });
    y += 7; total += (e.amount || 0);
  });

  y += 4; doc.setDrawColor(220, 220, 220); doc.line(18, y, pageWidth - 18, y); y += 8;
  doc.setFontSize(11); doc.setFont(undefined, 'bold');
  doc.text(`Total affiché : ${total.toLocaleString()} CFA`, pageWidth - 20, y, { align: 'right' }); y += 7;
  doc.setTextColor(16, 129, 96);
  doc.text(`Total approuvé (sorties de caisse) : ${approvedTotal.toLocaleString()} CFA`, pageWidth - 20, y, { align: 'right' });

  doc.setTextColor(150, 150, 150); doc.setFontSize(8); doc.setFont(undefined, 'normal');
  doc.rawText(`${gymName} — ${pdfText("Généré le")} ${new Date().toLocaleString(documentLocale())}`, 20, pageHeight - 10);
  doc.save(`Depenses_${gymName}_${new Date().toISOString().split('T')[0]}.pdf`);
  toast.success('Liste des dépenses exportée en PDF');
};


const localizedMonths = () => Array.from({ length: 12 }, (_, month) => new Intl.DateTimeFormat(documentLocale(), { month: 'short' }).format(new Date(2024, month, 1)));

export const generateAnnualReportPDF = (report = {}, gymName = 'Racinefit') => {
  const doc = localizedPdf(new jsPDF());
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let y = brandedHeader(doc, 'Rapport financier annuel');
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(16); doc.setFont(undefined, 'bold');
  doc.text(`Rapport financier annuel ${report.year || ''}`, 20, y); y += 10;

  const revenue = report.revenue || Array(12).fill(0);
  const expenses = report.expenses || Array(12).fill(0);
  const net = report.net || revenue.map((r, i) => r - (expenses[i] || 0));

  // Encadrés totaux
  const boxes = [
    { label: 'Recettes', val: report.total_revenue || 0, col: [16, 129, 96], bg: [240, 253, 244] },
    { label: 'Dépenses', val: report.total_expenses || 0, col: [220, 38, 38], bg: [254, 242, 242] },
    { label: 'Solde net', val: report.total_net || 0, col: (report.total_net || 0) >= 0 ? [16, 129, 96] : [220, 38, 38], bg: [239, 246, 255] },
  ];
  const bw = (pageWidth - 40 - 10) / 3;
  boxes.forEach((b, i) => {
    const x = 20 + i * (bw + 5);
    doc.setFillColor(...b.bg); doc.roundedRect(x, y, bw, 22, 3, 3, 'F');
    doc.setFontSize(9); doc.setFont(undefined, 'normal'); doc.setTextColor(90, 90, 90);
    doc.text(b.label, x + 4, y + 8);
    doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.setTextColor(...b.col);
    doc.text(`${b.val.toLocaleString()} CFA`, x + 4, y + 17);
  });
  y += 30;
  doc.setTextColor(0, 0, 0);

  // Comparaison avec l'année précédente
  if (report.prev) {
    doc.setFontSize(11); doc.setFont(undefined, 'bold'); doc.setTextColor(37, 99, 235);
    doc.rawText(`${pdfText("Évolution vs")} ${report.prev_year || pdfText('année préc.')}`, 20, y); y += 6;
    doc.setFontSize(9.5); doc.setFont(undefined, 'normal'); doc.setTextColor(60, 60, 60);
    doc.text(`• Recettes : ${comparisonLabel(report.total_revenue, report.prev.total_revenue, String(report.prev_year || 'an préc.'))}`, 24, y); y += 5.5;
    doc.text(`• Dépenses : ${comparisonLabel(report.total_expenses, report.prev.total_expenses, String(report.prev_year || 'an préc.'))}`, 24, y); y += 5.5;
    doc.text(`• Solde net : ${comparisonLabel(report.total_net, report.prev.total_net, String(report.prev_year || 'an préc.'))}`, 24, y); y += 8;
    doc.setTextColor(0, 0, 0);
  }

  // Graphique mensuel recettes (vert) vs dépenses (rouge)
  doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.setTextColor(37, 99, 235);
  doc.text('Recettes et dépenses par mois', 20, y); y += 4;
  doc.setTextColor(0, 0, 0);
  const chartH = 55, chartW = pageWidth - 40, baseY = y + chartH + 6;
  const maxV = Math.max(1, ...revenue, ...expenses);
  const groupW = chartW / 12;
  const barW = groupW / 3;
  doc.setDrawColor(220, 220, 220); doc.line(20, baseY, 20 + chartW, baseY);
  localizedMonths().forEach((mn, i) => {
    const gx = 20 + i * groupW + groupW / 2 - barW;
    const rh = (revenue[i] / maxV) * chartH;
    const eh = (expenses[i] / maxV) * chartH;
    doc.setFillColor(16, 129, 96); doc.rect(gx, baseY - rh, barW, rh, 'F');
    doc.setFillColor(220, 38, 38); doc.rect(gx + barW + 1, baseY - eh, barW, eh, 'F');
    doc.setFontSize(7); doc.setTextColor(120, 120, 120);
    doc.text(mn, 20 + i * groupW + groupW / 2, baseY + 5, { align: 'center' });
  });
  // Légende
  doc.setFontSize(8); doc.setFont(undefined, 'normal');
  doc.setFillColor(16, 129, 96); doc.rect(20, baseY + 9, 4, 4, 'F'); doc.setTextColor(0, 0, 0); doc.text('Recettes', 26, baseY + 12.5);
  doc.setFillColor(220, 38, 38); doc.rect(60, baseY + 9, 4, 4, 'F'); doc.text('Dépenses', 66, baseY + 12.5);
  y = baseY + 20;

  // Tableau détaillé par mois
  if (y > pageHeight - 90) { doc.addPage(); y = 20; }
  doc.setFontSize(12); doc.setFont(undefined, 'bold'); doc.setTextColor(234, 88, 12);
  doc.text('Détail mensuel', 20, y); y += 6;
  const cols = [{ t: 'Mois', x: 22 }, { t: 'Recettes', x: 70, a: 'right' }, { t: 'Dépenses', x: 115, a: 'right' }, { t: 'Net', x: 160, a: 'right' }, { t: 'Entrées', x: pageWidth - 22, a: 'right' }];
  doc.setFillColor(234, 88, 12); doc.rect(18, y - 4, pageWidth - 36, 8, 'F');
  doc.setFontSize(9); doc.setTextColor(255, 255, 255);
  cols.forEach((c) => doc.text(c.t, c.x, y, c.a ? { align: c.a } : undefined));
  y += 7; doc.setTextColor(0, 0, 0); doc.setFont(undefined, 'normal');
  const checkinsArr = report.checkins || Array(12).fill(0);
  localizedMonths().forEach((mn, i) => {
    if (y > pageHeight - 20) { doc.addPage(); y = 20; }
    if (i % 2 === 0) { doc.setFillColor(248, 248, 248); doc.rect(18, y - 4, pageWidth - 36, 7, 'F'); }
    doc.setTextColor(0, 0, 0);
    doc.text(mn, 22, y);
    doc.text(revenue[i].toLocaleString(), 70, y, { align: 'right' });
    doc.text(expenses[i].toLocaleString(), 115, y, { align: 'right' });
    doc.setTextColor(...(net[i] >= 0 ? [16, 129, 96] : [220, 38, 38]));
    doc.text(net[i].toLocaleString(), 160, y, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    doc.text(String(checkinsArr[i]), pageWidth - 22, y, { align: 'right' });
    y += 7;
  });

  doc.setTextColor(150, 150, 150); doc.setFontSize(8);
  doc.rawText(`${gymName} — ${report.total_new_members || 0} ${pdfText("nouveaux membres")} · ${report.total_checkins || 0} ${pdfText("entrées en")} ${report.year || ''} · ${pdfText("généré le")} ${new Date().toLocaleString(documentLocale())}`, 20, pageHeight - 10);
  doc.save(`Rapport_annuel_${gymName}_${report.year || ''}.pdf`);
  toast.success('Rapport annuel généré');
};

